import { CreatePage } from "./CreatePage";
import { ClientWorkflow, CreateWorkflowInput, CreateWorkflowInputType, TimeUnit } from "@avenueops/shared-types";
import { zodResolver } from "@hookform/resolvers/zod";
import CreateVisualQuery from "./CreateVisualQuery";
import { getDefaultNotificationLevel } from "@avenueops/frontend-shared";
import { PostgresQueryCompiler, QueryAst } from "@avenueops/visual-query-editor";
import {
  DataViewType,
  FilterMatchType,
  MessageFormattingType,
  MetricAggregationFunction,
  MetricComparisonOperator,
  Organization,
  WorkflowAlertingConditions,
  WorkflowTriggerType,
  WorkflowType,
} from "@prisma/client";
import { Prisma } from "@prisma/client";
import { useQueryClient } from "@tanstack/react-query";
import { useOrganization } from "context/OrganizationContext";
import { QueryRunProvider } from "context/QueryRunContext";
import { useRouter } from "next/router";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { BASE_API_URL } from "utils/constants";
import { getConnectionChoices } from "utils/getConnectionChoices";
import getScheduleDefaults from "utils/getScheduleDefaults";
import secondsToTimeUnitValue from "utils/secondsToTimeUnitValue";
import { trpc, trpc2 } from "utils/trpc";
import { getDefaultTitleFormat, getIntegrationWebhookTrigger, getTriggerType } from "utils/workflows";
import { v4 } from "uuid";
import _ from "lodash";

export const DEFAULT_EMOJI = "🚨";
interface DefaultValueProps {
  monitor: Partial<ClientWorkflow & Record<string, any>>;
  draftId?: string;
  connections?: any[];
  organization?: Organization;
}

export function getDefaultCreateWorkflowInput({ monitor, draftId, connections = [], organization }: DefaultValueProps) {
  const queryMeta = (draftId ? monitor?.queryMeta : (monitor?.dataView?.queryMeta as Prisma.JsonObject)) ?? {};
  const metricLookback = secondsToTimeUnitValue(monitor?.metricLookbackSeconds!);

  return {
    timeZoneOffset: new Date().getTimezoneOffset(),
    // 1. Describe the monitor
    title: monitor?.title,
    description: monitor?.description ?? undefined,
    emoji: monitor?.emoji ?? DEFAULT_EMOJI,
    workflowType: monitor?.workflowType ?? WorkflowType.EVENT,
    monitorGroupId: monitor?.monitorGroupId ?? undefined,
    tagIds: monitor?.tags ? monitor.tags.map((tag) => tag.id) : [],

    // 2. Select the data source
    databaseId: draftId
      ? monitor?.databaseId
      : monitor?.dataView?.databaseConnectionId
      ? monitor.dataView.databaseConnectionId
      : connections.length > 0
      ? getConnectionChoices(connections)[0].value
      : undefined,
    queryMeta: {
      includeHeaders: queryMeta.includeHeaders ?? true,
      isAggregation: queryMeta.isAggregation ?? false,
      workbook: queryMeta.workbook ?? null,
      view: queryMeta.view ?? null,
    },
    // Data View / SQL Query
    dataViewType: draftId ? monitor?.dataViewType : monitor?.dataView?.type ?? DataViewType.SQL,
    visualQueryBlob: draftId ? monitor?.visualQueryBlob : monitor?.dataView?.visualQueryBlob ?? undefined,
    dataViewId: monitor?.dataViewId ?? undefined,
    query: (draftId ? monitor?.query : monitor?.dataView?.query) ?? undefined,
    idColumns: draftId ? monitor?.idColumns ?? [] : monitor?.dataView?.idColumns,
    // Webhook
    webhook: draftId
      ? monitor?.webhook
      : monitor?.webhookTrigger?.slug
      ? `${BASE_API_URL}/trigger/${monitor?.webhookTrigger?.slug}`
      : `${BASE_API_URL}/trigger/${v4()}`,
    formTemplateId: monitor?.formTemplateId ?? undefined,
    formTemplateJson: monitor?.formTemplate?.legacySlackBlocks ? JSON.stringify(monitor.formTemplate.legacySlackBlocksJson, null, 2) : undefined,
    // Visual Editor
    filters: (draftId ? monitor?.filters : monitor?.dataView?.relationalQuery?.filters) ?? [],
    data: monitor?.dataView?.relationalQuery
      ? {
          table: monitor?.dataView?.relationalQuery?.tableName,
          columns: monitor?.dataView?.relationalQuery?.fields ?? ["id"],
        }
      : undefined,
    dynamicColumns: (draftId ? monitor?.dynamicColumns : monitor?.dynamicTags?.map((dynamicTag) => dynamicTag.workflowColumn)) ?? [],
    enrichment: draftId
      ? monitor?.enrichment
        ? monitor.enrichment
        : undefined
      : monitor?.enrichments && monitor.enrichments.length > 0
      ? {
          databaseId: monitor?.enrichments[0].dataView.databaseConnectionId,
          query: monitor?.enrichments[0].dataView.query,
          queryMeta: {
            includeHeaders: (monitor?.enrichments[0].dataView.queryMeta as Prisma.JsonObject | undefined)?.includeHeaders ?? true,
            isAggregation: (monitor?.enrichments[0].dataView.queryMeta as Prisma.JsonObject | undefined)?.isAggregation ?? false,
            workbook: (monitor?.enrichments[0].dataView.queryMeta as Prisma.JsonObject | undefined)?.workbook ?? null,
            view: (monitor?.enrichments[0].dataView.queryMeta as Prisma.JsonObject | undefined)?.view ?? null,
          },
          dataColumn: monitor?.enrichments[0].dataColumn,
          enrichmentColumn: monitor?.enrichments[0].enrichmentColumn,
          joinType: monitor?.enrichments[0].joinType,
        }
      : undefined,

    // 3. Configure Alert Conditions
    workflowTriggerType: getTriggerType(draftId, monitor),
    integrationWebhookTrigger: getIntegrationWebhookTrigger(draftId, monitor),
    hideViewDetails: monitor?.hideViewDetails ?? false,
    alertingConditions: monitor?.alertingConditions ?? WorkflowAlertingConditions.NEW_ROW,
    ...getScheduleDefaults(
      monitor?.schedule ?? {
        id: "",
        frequency: monitor?.frequency,
        hour: monitor?.hour,
        days: monitor?.days,
        timezone: monitor?.timezone,
        cronExpression: monitor?.cronExpression,
        gmtOffset: monitor?.gmtOffset,
      },
      organization,
    ),
    aggregateResults: monitor?.aggregateResults ?? false,
    rowFilters: monitor?.rowFilters ?? [],
    rowFilterMatchType: monitor?.rowFilterMatchType ?? FilterMatchType.ALL,
    // Metrics alert conditions
    metricId: monitor?.metricId ?? undefined,
    metricTimestampColumn: (draftId ? monitor?.metricTimestampColumn : monitor?.metric?.timestampColumn) ?? undefined,
    metricValueColumn: (draftId ? monitor?.metricValueColumn : monitor?.metric?.valueColumn) ?? undefined,
    metricThreshold: (draftId ? monitor?.metricThreshold : monitor?.metricThreshold) ?? undefined,
    metricLookbackValue: (draftId ? monitor?.metricLookbackValue : metricLookback?.value) ?? 60,
    metricLookbackUnit: (draftId ? monitor?.metricLookbackUnit : metricLookback?.unit) ?? TimeUnit.MINUTES,
    metricAggregationFunction: monitor?.metricAggregationFunction ?? MetricAggregationFunction.SUM,
    metricComparisonOperator: monitor?.metricComparisonOperator ?? MetricComparisonOperator.GREATER_THAN,
    metricPercentile: monitor?.metricPercentile ?? 90,
    metricRunsToHealthy: monitor?.metricRunsToHealthy?.toString() ?? undefined,

    priority: monitor?.priority ?? "P2",
    autoAssignee: draftId ? monitor?.autoAssignee : monitor?.WorkflowAutoAssignmentPolicy ?? undefined,
    shouldAutoResolve: monitor?.shouldAutoResolve ?? false,

    // 5. Format Alert
    messageFormat: monitor?.messageFormat,
    messageFormattingType: monitor?.messageFormattingType ?? MessageFormattingType.PER_ROW,
    titleFormat: monitor?.titleFormat ?? getDefaultTitleFormat(DEFAULT_EMOJI, ""),
    // Meta fields (not for submission, controls composite fields)
    // TODO: Update this field to replace current workflowType property (https://linear.app/avenue/issue/AVE-3950/update-workflow-type-to-reflect-actual-workflow-type)
    _workflowType: draftId
      ? monitor?._workflowType
      : monitor?.triggerType === WorkflowTriggerType.WEBHOOK && monitor?.formTemplateId !== null
      ? "FORM"
      : monitor?.triggerType === WorkflowTriggerType.WEBHOOK && monitor?.formTemplateId === null
      ? "WEBHOOK"
      : monitor?.triggerType === WorkflowTriggerType.INTEGRATION
      ? _.toUpper(monitor?.integrationWebhookTrigger?.triggerEvent) ?? "INTEGRATION"
      : monitor?.alertingConditions === WorkflowAlertingConditions.PASSTHROUGH
      ? "REPORT"
      : monitor?.alertingConditions === WorkflowAlertingConditions.METRIC_THRESHOLD
      ? "METRIC"
      : "EVENT",
  };
}
interface Props {
  monitor: Partial<ClientWorkflow & Record<string, any>>;
  draftId?: string;
  isDuplicate?: boolean;
  connections?: any[];
}

export function MonitorEditForm({ monitor, draftId, isDuplicate, connections = [] }: Props) {
  // If we are creating a new monitor, and the dataview type is visual, show the standalone visual query builder first
  const [isVisualQueryBuilder, setIsVisualQueryBuilder] = useState<boolean>(!!draftId && monitor.dataViewType === DataViewType.VISUAL);
  const [isEditPage, setIsEditPage] = useState<boolean>(true);
  const [savedDraft, setSavedDraft] = useState<Partial<CreateWorkflowInputType>>();

  const utils2 = trpc2.useContext();
  const utils = trpc.useContext();
  const queryClient = useQueryClient();
  const router = useRouter();

  const organization = useOrganization();

  const { mutateAsync: updateDraft, isLoading: updatedDraftLoading } = trpc2.v2.workflows.drafts.update.useMutation({
    onSuccess: async (response: any) => {
      await utils2.v2.workflows.drafts.get.invalidate({ draftId: draftId! });
    },
  });

  // called when submitted from review page
  const { mutateAsync: createWorkflow, isLoading: createWorkflowLoading } = trpc2.v2.workflows.create.useMutation({
    onSuccess: async (response: any) => {
      if (monitor?.id) {
        await utils.workflowAutoAssign.get.invalidate({ workflowId: monitor.id });
        await utils.workflowAutoAssign.get.refetch({ workflowId: monitor.id });
      }
      await utils2.v2.databaseConnections.list.invalidate();
      await queryClient.invalidateQueries(monitor?.id ? ["signals", monitor.id] : ["signals"]);
      router.push(`/monitors/${response.id}`);
      toast.success("Monitor was created!");
    },
  });

  const { mutateAsync: updateWorkflow, isLoading: updateWorkflowLoading } = trpc2.v2.workflows.update.useMutation({
    onSuccess: async (response: any) => {
      await queryClient.invalidateQueries(monitor?.id ? ["signals", monitor.id] : ["signals"]);
      await utils2.v2.workflows.get.invalidate();
      router.push(`/monitors/${response.id}`);
      toast.success("Monitor was updated!");
    },
  });

  const onAutoSave = async (data: any) => {
    setSavedDraft(data);
    if (draftId && data) {
      updateDraft({ formData: { ...monitor, ...data }, draftId });
    }
  };

  const onVQEAdvance = async (data: any) => {
    setSavedDraft(data);
    setIsVisualQueryBuilder(false);
    setIsEditPage(true);
  };

  const onSubmit = async (data: any) => {
    data.timeZoneOffset = new Date().getTimezoneOffset();

    try {
      data.forkParentId = monitor?.forkParentId ?? undefined;
      if (monitor?.id) {
        data.workflowId = monitor.id;
        await updateWorkflow({ ...data, ...savedDraft });
      } else {
        data.draftId = draftId;
        await createWorkflow({ ...data, ...savedDraft });
      }
    } catch (error: any) {
      toast.error(error?.data?.friendlyMessage ?? "Something went wrong. Please try again later.");
    }
  };

  const defaultValues = getDefaultCreateWorkflowInput({ monitor, draftId, organization, connections });

  const isLoading = updatedDraftLoading || createWorkflowLoading || updateWorkflowLoading;

  const formMethods = useForm<Partial<CreateWorkflowInputType>>({
    defaultValues: savedDraft ? savedDraft : (defaultValues as Partial<CreateWorkflowInputType>) ?? {},
    resolver: zodResolver(CreateWorkflowInput),
  });

  return (
    <QueryRunProvider workflowId={undefined}>
      <FormProvider {...formMethods}>
        {isVisualQueryBuilder ? (
          <CreateVisualQuery onAutoSave={onAutoSave} onAdvance={onVQEAdvance} defaultValues={defaultValues} />
        ) : (
          <form id="MonitorEditFormCreatePage" onSubmit={formMethods.handleSubmit(onSubmit)}>
            <CreatePage saveDraft={draftId ? onAutoSave : undefined} backTo={draftId ? "/monitors" : `/monitors/${monitor.id!}`} />
          </form>
        )}
      </FormProvider>
    </QueryRunProvider>
  );
}
